import type { CellContext, ColumnDef } from '@tanstack/react-table'

import { TableSelectAllCheckbox, TableSelectRowCheckbox } from '@/components/ui'
import type { Check, Transaction } from '@/modules/projects/stores'
import { AccountTypeBadgeCell } from './AccountTypeBadgeCell'
import { AmountCell } from './AmountCell'
import { ContextMenuCell } from './ContextMenuCell'
import { DescriptionCell } from './DescriptionCell'
import { DirectionCell } from './DirectionCell'
import { DocumentCell } from './DocumentCell'
import { FavoriteCell } from './FavoriteCell'
import { FavoriteHeader } from './FavoriteHeader'
import { KindBadgeCell } from './KindBadgeCell'
import { RowActionsCell } from './RowActionsCell'

// Plain text cell, no ContextMenu wrapper. Chat artifacts use this
// instead of ContextMenuCell(…) because right-click edit/delete don't
// belong on an evidentiary snapshot — and mounting one Radix popover
// per cell × many rows was the dominant cost in expanded mode.
const PlainTextCell = (cell: CellContext<Transaction, unknown>) => {
  const value = cell.getValue()
  if (value === null || value === undefined || value === '') {
    return (
      <span aria-hidden="true" className="text-muted-foreground select-none">
        —
      </span>
    )
  }
  return <span className="block truncate">{String(value)}</span>
}

export type TransactionsTableVariant = 'transactions' | 'checks'

// `variant: 'checks'` drops the Account Type column: a check carries the
// drawer's account, and we have no signal for what kind of account that is.
export const transactionsColumns: (
  hasShareNumber: boolean,
  variant?: TransactionsTableVariant,
) => ColumnDef<Transaction, any>[] = (
  hasShareNumber,
  variant = 'transactions',
) => [
  {
    id: 'select',
    enableSorting: false,
    enableHiding: false,
    size: 50,
    header: TableSelectAllCheckbox,
    cell: TableSelectRowCheckbox,
  },
  // Favorites live on the transactions slice (the `tags` field + the
  // transactions endpoint). Checks carry no tags and have their own ids, so
  // the star is dropped for the checks variant rather than mutating an
  // unrelated same-id transaction.
  ...(variant === 'checks'
    ? []
    : [
        {
          id: 'tags',
          enableSorting: false,
          enableHiding: false,
          size: 50,
          header: FavoriteHeader,
          cell: FavoriteCell,
        },
      ]),
  {
    accessorKey: 'date',
    header: 'Date',
    size: 120,
    filterFn: 'custom' as any,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 350,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(DescriptionCell),
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    size: 120,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(AmountCell),
  },
  {
    accessorKey: 'kind',
    header: 'Kind',
    size: 100,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(KindBadgeCell),
  },
  // Direction (in / out) lives only on checks; an unresolved check is flagged for review.
  ...(variant === 'checks'
    ? [
        {
          id: 'direction',
          header: 'Direction',
          size: 130,
          enableSorting: false,
          cell: DirectionCell,
        } as ColumnDef<Transaction, any>,
      ]
    : []),
  {
    accessorKey: 'merchant',
    header: 'Merchant',
    size: 150,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'category',
    header: 'Category',
    size: 150,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'categoryDetail',
    header: 'Category Detail',
    size: 150,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'paymentChannel',
    header: 'Payment Channel',
    size: 150,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'institution',
    header: 'Institution',
    size: 175,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'accountOwner',
    header: 'Account Owner',
    size: 175,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'accountNumber',
    header: 'Account',
    size: 100,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  ...(hasShareNumber
    ? [
        {
          accessorKey: 'shareNumber',
          header: 'Share Number',
          size: 150,
          filterFn: 'custom' as any,
          cell: ContextMenuCell(),
        },
      ]
    : []),
  ...(variant === 'checks'
    ? []
    : [
        {
          accessorKey: 'accountType',
          header: 'Account Type',
          size: 120,
          filterFn: 'custom' as any,
          cell: ContextMenuCell(AccountTypeBadgeCell),
        },
      ]),
  {
    accessorKey: 'statementFileName',
    header: 'Document',
    meta: { isFluid: true },
    size: 350,
    filterFn: 'custom' as any,
    cell: DocumentCell,
  },
  {
    accessorKey: 'sourcePage',
    header: 'Page Number',
    size: 120,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'pageOrder',
    header: 'Row On Page',
    size: 120,
    filterFn: 'custom' as any,
    cell: ContextMenuCell(),
  },
  {
    accessorKey: 'isNotCCPayment',
    enableHiding: false,
    enableSorting: false,
    filterFn: 'custom' as any,
  },
  {
    id: 'actions',
    header: '',
    size: 56,
    enableSorting: false,
    enableHiding: false,
    cell: RowActionsCell,
  },
]

// Read-only column set for chat artifacts. Same visual primitives as
// the Overview table, but stripped of every per-row interactive
// affordance — no select checkbox, no favorite star, no per-cell
// ContextMenu. The full transactionsColumns mounted ~11 Radix popovers
// per row, which made the expanded chat dialog (~20 visible rows)
// noticeably choppy. The Document column is kept as a link because
// jumping to the source page is the one read-only affordance an
// evidence snapshot actually wants.
export const chatTransactionsColumns: (
  hasShareNumber: boolean,
) => ColumnDef<Transaction, any>[] = (hasShareNumber) => [
  {
    accessorKey: 'date',
    header: 'Date',
    size: 110,
    cell: PlainTextCell,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 320,
    cell: DescriptionCell,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    size: 120,
    cell: AmountCell,
  },
  {
    accessorKey: 'kind',
    header: 'Kind',
    size: 90,
    cell: KindBadgeCell,
  },
  {
    accessorKey: 'category',
    header: 'Category',
    size: 140,
    cell: PlainTextCell,
  },
  {
    accessorKey: 'categoryDetail',
    header: 'Category Detail',
    size: 140,
    cell: PlainTextCell,
  },
  {
    accessorKey: 'accountNumber',
    header: 'Account',
    size: 100,
    cell: PlainTextCell,
  },
  ...(hasShareNumber
    ? [
        {
          accessorKey: 'shareNumber',
          header: 'Share',
          size: 100,
          cell: PlainTextCell,
        } as ColumnDef<Transaction, any>,
      ]
    : []),
  {
    accessorKey: 'accountType',
    header: 'Account Type',
    size: 110,
    cell: AccountTypeBadgeCell,
  },
  {
    accessorKey: 'statementFileName',
    header: 'Document',
    meta: { isFluid: true },
    size: 280,
    cell: DocumentCell,
  },
]

// Read-only column set for the chat checks artifact. Mirrors
// chatTransactionsColumns but swaps the Account Type column (a check
// carries no account-type signal) for the check number, since that's
// the identifier an analyst reads a check by. Typed against Transaction
// so the shared chat cells apply unchanged; checkNumber lives only on
// Check, so it's read through an accessorFn.
export const chatChecksColumns: () => ColumnDef<Transaction, any>[] = () => [
  {
    accessorKey: 'date',
    header: 'Date',
    size: 110,
    cell: PlainTextCell,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 320,
    cell: DescriptionCell,
  },
  {
    id: 'checkNumber',
    header: 'Check #',
    size: 100,
    accessorFn: (row) => (row as unknown as Check).checkNumber,
    cell: PlainTextCell,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    size: 120,
    cell: AmountCell,
  },
  {
    accessorKey: 'kind',
    header: 'Kind',
    size: 90,
    cell: KindBadgeCell,
  },
  {
    accessorKey: 'accountNumber',
    header: 'Account',
    size: 100,
    cell: PlainTextCell,
  },
  {
    accessorKey: 'statementFileName',
    header: 'Document',
    meta: { isFluid: true },
    size: 280,
    cell: DocumentCell,
  },
]
